/**
 * LightningReflexesSkillEffect — Lightning tree Discharge path fork-2 active.
 *
 * Passive-reactive counter-attack: when the player dashes through an enemy's
 * active attack arc, the body fires back automatically. A static bolt scales
 * with stored charge — no button press required. 300ms detection window after
 * each dash, forgiving timing. Cooldown governs proc frequency, not dash rate.
 *
 * Activation trigger: automatic on condition. The integration layer calls
 * onDash() at dash start. During the detection window, onAttackArcDetected()
 * is called if an enemy attack arc overlaps the dash trajectory. If detected
 * and off cooldown: auto-fire a bolt toward the attacker, begin cooldown.
 *
 * Damage = baseReflexDamage × (chargeLevel / maxCharge) × damageMultiplier.
 * Charge is read but NOT consumed — the bolt is free power from the dodge.
 *
 * Upgrade keys (via resolver, scope: 'lightning_reflexes'):
 * - detectionWindowMs: additive — added to base 300ms window (Hair Trigger: +100ms)
 * - damageMultiplier: multiplicative — bolt damage scaling (Hair Trigger: 1.4×)
 * - chargeOnProc: additive — fraction of dashChargeRate added on proc (Capacitor: 0.5)
 *
 * Implements SkillEffect directly with own cooldown management
 * (InstantSkillEffect has no cooldown). Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see lightning-spec.md § Discharge Path — Fork 2: Lightning Reflexes Sub-Path
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface LightningReflexesProjectileConfig {
  readonly damage: number;
  readonly sourceSkillId: string;
  readonly targetEntityId: string;
}

export interface LightningReflexesDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly projectiles: {
    /** Fire a reflex bolt at the target. Returns enemies hit (0 = miss). */
    fire(config: LightningReflexesProjectileConfig): number;
  };
  readonly charge: {
    /** Read current charge without consuming. */
    getCurrent(chargeId: string): number;
    /** Read max charge capacity. */
    getMax(chargeId: string): number;
    /** Add charge (for Capacitor proc). */
    addCharge(chargeId: string, amount: number): void;
  };
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface LightningReflexesConfig {
  /** Long cooldown tier: 2000ms. */
  readonly cooldownMs: number;
  /** Base detection window in ms after dash starts. */
  readonly baseDetectionWindowMs: number;
  /** Base damage of the reflex bolt before charge scaling. */
  readonly baseReflexDamage: number;
  /** Dash charge rate — used for Capacitor charge gain calculation. */
  readonly dashChargeRate: number;
}

const DEFAULT_CONFIG: LightningReflexesConfig = {
  cooldownMs: 2000,
  baseDetectionWindowMs: 300,
  baseReflexDamage: 15,
  dashChargeRate: 15,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class LightningReflexesSkillEffect implements SkillEffect {
  readonly skillId = 'lightning_reflexes';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: LightningReflexesDeps;
  private readonly config: LightningReflexesConfig;

  private _cooldownRemaining = 0;
  private _windowOpen = false;
  private _windowTimer = 0;
  private _procsFired = 0;
  private _hitsLanded = 0;
  private _fizzles = 0;
  private _chargeGained = 0;
  private _lastBoltDamage = 0;
  private _lastChargeRatio = 0;
  private _resolvedWindowMs = 0;
  private _resolvedDamageMultiplier = 1.0;

  constructor(deps: LightningReflexesDeps, config: Partial<LightningReflexesConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // -- Read-only accessors for simulation inspection --------------------------

  get cooldown(): number { return this._cooldownRemaining; }
  get windowOpen(): boolean { return this._windowOpen; }
  get windowTimer(): number { return this._windowTimer; }
  get procsFired(): number { return this._procsFired; }
  get hitsLanded(): number { return this._hitsLanded; }
  get fizzles(): number { return this._fizzles; }
  get chargeGained(): number { return this._chargeGained; }
  get lastBoltDamage(): number { return this._lastBoltDamage; }
  get lastChargeRatio(): number { return this._lastChargeRatio; }
  get resolvedWindowMs(): number { return this._resolvedWindowMs; }
  get resolvedDamageMultiplier(): number { return this._resolvedDamageMultiplier; }

  // -- Dash event hook (called by integration layer) --------------------------

  /**
   * Called by the integration layer whenever the player dashes. Opens the
   * detection window if not on cooldown. The window stays open for
   * baseDetectionWindowMs + detectionWindowMs (from Hair Trigger upgrade).
   */
  onDash(): void {
    if (this._cooldownRemaining > 0) return;
    if (this._windowOpen) return; // already open from a concurrent dash

    // Resolve detection window duration
    const bonusMs = this.deps.resolver.getAdditiveEffect(
      'lightning_reflexes', 'detectionWindowMs',
    );
    this._resolvedWindowMs = this.config.baseDetectionWindowMs + bonusMs;

    this._windowOpen = true;
    this._windowTimer = this._resolvedWindowMs;
  }

  /**
   * Called by the detection system when an enemy's attack arc overlaps the
   * dash trajectory during an open detection window. If conditions are met,
   * auto-fires a reflex bolt at the attacker.
   *
   * @param targetEntityId - the entity whose attack arc was dodged
   */
  onAttackArcDetected(targetEntityId: string): void {
    if (!this._windowOpen) return;
    if (this._cooldownRemaining > 0) return;

    // Fizzle check
    if (!this.deps.rollSkillSuccess('lightning_reflexes')) {
      this._fizzles++;
      this._windowOpen = false;
      this._windowTimer = 0;
      return;
    }

    this.deps.breakHide?.();

    // Read current charge (not consumed)
    const currentCharge = this.deps.charge.getCurrent('static_charge');
    const maxCharge = this.deps.charge.getMax('static_charge');
    const chargeRatio = maxCharge > 0 ? Math.min(currentCharge / maxCharge, 1.0) : 0;
    this._lastChargeRatio = chargeRatio;

    // Compute bolt damage with charge scaling and upgrade multiplier
    this._resolvedDamageMultiplier = this.deps.resolver.getMultiplicativeEffect(
      'lightning_reflexes', 'damageMultiplier',
    );
    this._lastBoltDamage = this.config.baseReflexDamage * chargeRatio * this._resolvedDamageMultiplier;

    // Fire the reflex bolt
    const enemiesHit = this.deps.projectiles.fire({
      damage: this._lastBoltDamage,
      sourceSkillId: 'lightning_reflexes',
      targetEntityId,
    });

    this._procsFired++;
    if (enemiesHit > 0) {
      this._hitsLanded++;
    }

    // Capacitor: add charge on successful proc
    const chargeOnProc = this.deps.resolver.getAdditiveEffect(
      'lightning_reflexes', 'chargeOnProc',
    );
    if (chargeOnProc > 0) {
      const chargeGain = this.config.dashChargeRate * chargeOnProc;
      this.deps.charge.addCharge('static_charge', chargeGain);
      this._chargeGained += chargeGain;
    }

    // Close window and begin cooldown
    this._windowOpen = false;
    this._windowTimer = 0;
    this._cooldownRemaining = this.config.cooldownMs;
  }

  // -- SkillEffect interface --------------------------------------------------

  /**
   * Lightning Reflexes cannot be manually activated. It fires automatically
   * from dash-through detection. Always returns false for manual activation.
   */
  canActivate(): boolean {
    return false;
  }

  activate(): boolean {
    return false;
  }

  deactivate(): void { /* passive-reactive — nothing to deactivate */ }
  isActive(): boolean { return false; }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick detection window
    if (this._windowOpen) {
      this._windowTimer = Math.max(0, this._windowTimer - delta);
      if (this._windowTimer <= 0) {
        this._windowOpen = false;
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._windowOpen = false;
    this._windowTimer = 0;
  }
}
